import { useState, useEffect, useRef, useMemo } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { SigningModal } from "./SigningModal";
import { Button } from "./Buttons/Button";
import { PDFDocument, rgb } from "pdf-lib";
import { PdfViewer } from "./ShowPdf";
import { lang } from "src/language/Strings";
import { isMobile } from "src/utils/isMobile";
import useFeatures from "src/api/hooks/useFeatures";
import { useSigningSession } from "src/api/hooks/useSigningSession";
import { ShowToast } from "./Toast";
import { routes } from "src/routes";
import useMyUser from "src/api/hooks/useMyUser";
import { SasFileResult } from "src/types/InspectionUpdateRequest";
import { queryKeys } from "src/api/queryKeys";
import { useQueryClient } from "@tanstack/react-query";

export function SigningPage() {
  const { data: features } = useFeatures();
  const signingSession = useSigningSession();
  const queryClient = useQueryClient();
  const [isSigningFile, setIsSigningFile] = useState(false);
  const [signatureImage, setSignatureImage] = useState<string | undefined>(
    undefined
  );
  const location = useLocation();
  const { data: myUser } = useMyUser();
  const { companyId, documentId, signingSessionId } = useParams();
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signedFile, setSignedFile] = useState<ArrayBuffer | null>();
  const currentDocument = useMemo<SasFileResult | undefined>(() => {
    const document = signingSession.get.data?.find(
      (d) => d.id === Number(documentId)
    );
    return document;
  }, [signingSession.get.data]);
  const [searchParams] = useSearchParams();
  const signatureForUser = useMemo(
    () => currentDocument?.signatures?.find((s) => s.UserId === myUser?.Id),
    [currentDocument, myUser?.Id]
  );

  useEffect(() => {
    if (signingSession.sign.status === "success") {
      queryClient.invalidateQueries({
        queryKey: queryKeys.files(companyId!, "all"),
      });
      navigate(routes.SigningSession.DocumentById.url(companyId!, documentId!));
      ShowToast("PDF signed successfully", "success", 3000);
    } else if (signingSession.sign.status === "error") {
      navigate(routes.SigningSession.DocumentById.url(companyId!, documentId!));
      ShowToast("Failed to sign PDF", "error", 3000);
    }
  }, [signingSession.sign.status]);

  useEffect(() => {
    const loadPdf = async () => {
      try {
        setLoading(true);
        setError(null);

        const pdfUrl = currentDocument?.signatures?.length
          ? currentDocument.signatures.findLast(
              (s) => s.signingStatus === "Success"
            )?.signedFile?.url || ""
          : currentDocument?.files[0] || "";
        const response = await fetch(pdfUrl);
        if (!response.ok) {
          throw new Error(`Failed to load PDF: ${response.statusText}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        setPdfBytes(bytes);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load PDF");
        console.error("Error loading PDF:", err);
      } finally {
        setLoading(false);
      }
    };

    loadPdf();
  }, [signingSession.get.data]);

  const handleSignatureSave = async (imageDataUrl: string) => {
    if (!pdfBytes) {
      setError("No PDF loaded to sign");
      return;
    }
    try {
      setLoading(true);
      setError(null);

      // Load the PDF document
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pages = pdfDoc.getPages();
      const lastPage = pages[pages.length - 1];
      const { width, height } = lastPage.getSize();

      // Convert data URL to PNG bytes
      const imageResponse = await fetch(imageDataUrl);
      const imageBytes = await imageResponse.arrayBuffer();

      const pngImage = await pdfDoc.embedPng(imageBytes);

      // Calculate signature size and position (bottom right corner)

      const signatureWidth = 150;
      const signatureHeight = 75;
      const margin = 20;
      const x = width - signatureWidth - margin;
      const y = margin;

      // Add signature to the last page
      lastPage.drawImage(pngImage, {
        x: x,
        y: y,
        width: signatureWidth,
        height: signatureHeight,
      });

      // Save the modified PDF
      const modifiedPdfBytes = await pdfDoc.save();

      // Create blob and download
      const uint8Array = new Uint8Array(modifiedPdfBytes);
      const blob = new Blob([uint8Array], { type: "application/pdf" });

      const url = URL.createObjectURL(blob);

      const downloadFile = () => {
        const link = document.createElement("a");
        link.href = url;
        link.download = `signed-document-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
      };

      // createReport.mutate({
      //   title: "Signatur",
      //   companyId: Number(companyId),
      //   status: 1,u
      //   userId: myUser?.id || 0,
      //   jsonContent: JSON.stringify({
      //     signatureImage: signatureImage,
      //     fileName: fileToSign.split("/").pop(),
      //     signedFile: signedFile
      //       ? URL.createObjectURL(new Blob([signedFile]))
      //       : "",
      //     signedFileUrl: fileToSign,
      //   }),
      // });
      setSignatureImage(imageDataUrl);
      signingSession.sign.mutate({
        payload: {
          nonce: signingSession.start.data?.nonce || "",
          signatureBase64: imageDataUrl,
        },
        signingSessionId: Number(signingSession.start.data?.id!),
      });
    } catch (err) {
      console.error("Error signing PDF:", err);
    } finally {
      setLoading(false);
    }
  };
  const navigate = useNavigate();
  return (
    <>
      {!signingSessionId ? (
        <div
          className={`flex flex-col items-center ${
            isMobile()
              ? "w-screen h-screen"
              : `w-[${
                  document.getElementById("pdf-canvas")?.offsetWidth
                }] h-full`
          }`}
        >
          {loading && (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-PrimaryGreen"></div>
              <span className="ml-2">Loading PDF...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              Error: {error}
            </div>
          )}

          {pdfBytes && !loading && (
            <>
              <div className="w-full h-full max-h-screen">
                <PdfViewer
                  file={
                    signedFile ||
                    signingSession.get.data?.find(
                      (d) => d.id === Number(documentId)
                    )?.files[0] ||
                    ""
                  }
                  overlayChildren={
                    <>
                      {!signatureForUser && (
                        <div className={"absolute bottom-5 right-5"}>
                          <button
                            onClick={() =>
                              navigate(
                                routes.SigningSession.Sign.url(
                                  companyId!,
                                  documentId!,
                                  signingSession.start.data?.id! || ""
                                )
                              )
                            }
                            className={`bg-PrimaryGreen text-White border-Grey4 p-2 rounded-xl `}
                            disabled={!!signatureForUser}
                            title={lang.signDocument}
                          >
                            {lang.signDocument}
                          </button>
                        </div>
                      )}
                      {!!signatureForUser && (
                        <div className="flex flex-col items-center gap-2 absolute bottom-5 left-5">
                          <span className="text-SkaneGreen bg-White bg-opacity-95 rounded-xl p-2">
                            ✓ {lang.signed}
                          </span>
                          <img
                            src={signatureForUser?.signatureFile?.url || ""}
                            alt="Signature"
                            className="w-20 h-10 border border-gray-300 rounded"
                          />
                        </div>
                      )}
                    </>
                  }
                />
              </div>
            </>
          )}
        </div>
      ) : (
        <SigningModal
          onSave={handleSignatureSave}
          documentTitle="Sign Document"
          setIsActive={setIsSigningFile}
        />
      )}
    </>
  );
}
